import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSocketContext } from "./SocketContext";

const TYPING_TTL_MS = 4000;

const TeamTypingContext = createContext(null);

function parseTypingPayload(payload) {
  if (typeof payload === "string") {
    try {
      return JSON.parse(payload);
    } catch {
      return null;
    }
  }
  return payload && typeof payload === "object" ? payload : null;
}

export function TeamTypingProvider({ currentUserId = null, children }) {
  const { socket } = useSocketContext();
  const [typingBySpace, setTypingBySpace] = useState({});
  const timersRef = useRef(new Map());

  const removeUser = useCallback((spaceId, userId) => {
    const sid = String(spaceId);
    const uid = String(userId);
    setTypingBySpace((prev) => {
      const spaceUsers = prev[sid];
      if (!spaceUsers?.[uid]) return prev;
      const nextSpace = { ...spaceUsers };
      delete nextSpace[uid];
      if (!Object.keys(nextSpace).length) {
        const next = { ...prev };
        delete next[sid];
        return next;
      }
      return { ...prev, [sid]: nextSpace };
    });
  }, []);

  const refreshUser = useCallback(
    (spaceId, userId, userName) => {
      const uid = String(userId);
      if (currentUserId != null && uid === String(currentUserId)) return;

      const sid = String(spaceId);
      const timerKey = `${sid}:${uid}`;

      setTypingBySpace((prev) => ({
        ...prev,
        [sid]: {
          ...(prev[sid] || {}),
          [uid]: {
            userId: uid,
            userName: userName || prev[sid]?.[uid]?.userName || "",
          },
        },
      }));

      const existing = timersRef.current.get(timerKey);
      if (existing) clearTimeout(existing);
      timersRef.current.set(
        timerKey,
        setTimeout(() => {
          timersRef.current.delete(timerKey);
          removeUser(sid, uid);
        }, TYPING_TTL_MS)
      );
    },
    [currentUserId, removeUser]
  );

  useEffect(() => {
    if (!socket) return undefined;

    const handleTyping = (payload) => {
      const data = parseTypingPayload(payload);
      if (!data) return;

      const sid = data.spaceId != null ? Number(data.spaceId) : null;
      if (!sid) return;

      const uid = data.userId;
      if (uid == null) return;

      if (data.isTyping === false) {
        const timerKey = `${sid}:${uid}`;
        const timer = timersRef.current.get(timerKey);
        if (timer) clearTimeout(timer);
        timersRef.current.delete(timerKey);
        removeUser(sid, uid);
        return;
      }

      refreshUser(sid, uid, data.userName);
    };

    socket.on("team_typing", handleTyping);
    return () => {
      socket.off("team_typing", handleTyping);
    };
  }, [socket, refreshUser, removeUser]);

  useEffect(
    () => () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
    },
    []
  );

  const getTypingUsers = useCallback(
    (spaceId) => {
      if (spaceId == null) return [];
      return Object.values(typingBySpace[String(spaceId)] || {});
    },
    [typingBySpace]
  );

  const value = useMemo(
    () => ({ typingBySpace, getTypingUsers }),
    [typingBySpace, getTypingUsers]
  );

  return <TeamTypingContext.Provider value={value}>{children}</TeamTypingContext.Provider>;
}

export function useTeamTypingUsers(spaceId) {
  const ctx = useContext(TeamTypingContext);
  if (!ctx || spaceId == null) return [];
  return ctx.getTypingUsers(spaceId);
}
